import { modelUrl } from "@/lib/ai/detection/model-url"
import * as tf from "@/lib/ai/detection/tf"
import { measured } from "@/utils/benchmark"
import { Rect } from "./face-detection.js"
import { clamp } from "@/utils/math.js"
import { logCullingInfo } from "../utils.js"
import { vec2 } from "gl-matrix"

// To be backward compatible, the order here is not the same as the model output order
export enum EyeStatus {
	closed = 0,
	barelyOpen = 1,
	halfOpen = 2,
	fullyOpen = 3,
	midBlink = 4,
	posingLookDown = 5,
	posingSmile = 6,
	posingBeClose = 7,
	sunglasses = 8,
	occluded = 9,
	tooBlurry = 10,
	notAFace = 11,
	squinting = 12,
}

export interface FaceScoringResult {
	eyeStatus: EyeStatus
	eyeEstScore: number
	expressionScore: number
	focusScoreFloat: number
}

export interface FaceScoringInput {
	image: ImageBitmap
	rect: Rect
	confidence: number
	adjustedGamma: number
}

// Define the score dictionary
const scoreDict: Record<EyeStatus, number> = {
	[EyeStatus.closed]: 1,
	[EyeStatus.midBlink]: 1,
	[EyeStatus.barelyOpen]: 1.2,
	[EyeStatus.halfOpen]: 2,
	[EyeStatus.fullyOpen]: 5,
	[EyeStatus.posingLookDown]: 3.5,
	[EyeStatus.posingSmile]: 4.5,
	[EyeStatus.posingBeClose]: 4.0,
	[EyeStatus.sunglasses]: 3,
	[EyeStatus.occluded]: 2.5,
	[EyeStatus.tooBlurry]: 2.5,
	[EyeStatus.notAFace]: 2.5,
	[EyeStatus.squinting]: 3,
}

export function isFaceNonCenter(rect: Rect) {
	const area = rect.width * rect.height
	const center: vec2 = [rect.x + rect.width / 2, rect.y + rect.height / 2]
	if (Math.abs(center[0] - 0.5) < 0.1 && center[1] < 0.9) return false
	const nonCenter =
		Math.abs(center[0] - 0.5) > 0.25 ||
		(Math.abs(center[1] - 0.3) > 0.3 && area > 0.01) ||
		center[1] > 0.8

	return nonCenter
}

export class FaceScoring {
	private model: tf.GraphModel | null = null

	cleanup() {
		this.model?.dispose()
		this.model = null
	}

	@measured()
	public async loadModel(onProgress?: (progress: number) => void) {
		if (this.model) {
			return this.model
		}

		this.model = await tf.loadGraphModelWithRetry(modelUrl("f_v4_dyn"), {
			onProgress: onProgress,
		})

		return this.model
	}

	public async process(
		image: ImageBitmap,
		options: {
			rects: Rect[]
			confidences: (number | undefined)[]
			adjustedGammas: number[]
		},
	): Promise<FaceScoringResult[]> {
		const { rects, confidences, adjustedGammas } = options
		return await this.processBatch(
			rects.map((rect, index) => ({
				image,
				rect,
				confidence: confidences[index] ?? 1,
				adjustedGamma: adjustedGammas[index] ?? 1,
			})),
			rects.length,
		)
	}

	@measured()
	public async processBatch(
		inputs: FaceScoringInput[],
		batchSize: number,
	): Promise<FaceScoringResult[]> {
		if (inputs.length === 0) return []
		if (batchSize < 1 || !Number.isInteger(batchSize))
			throw new Error(`Invalid face scoring batch size: ${batchSize}`)
		const model = await this.loadModel()
		const groupedInputs = new Map<
			ImageBitmap,
			Array<{ input: FaceScoringInput; resultIndex: number }>
		>()
		inputs.forEach((input, resultIndex) => {
			const group = groupedInputs.get(input.image)
			if (group) group.push({ input, resultIndex })
			else groupedInputs.set(input.image, [{ input, resultIndex }])
		})
		const orderedInputs = [...groupedInputs.values()].flat()
		const inputTensor = tf.tidy(() =>
			tf.concat(
				[...groupedInputs.entries()].map(([image, group]) =>
					tf.tidy(() => {
						const source = tf.browser.fromPixels(image).expandDims<tf.Tensor4D>()
						const crops = tf.image.cropAndResize(
							source,
							group.map(({ input }) => [
								input.rect.y,
								input.rect.x,
								input.rect.y + input.rect.height,
								input.rect.x + input.rect.width,
							]),
							group.map(() => 0),
							[224, 224],
						)
						if (group.every(({ input }) => input.adjustedGamma === 1))
							return crops.div(127.5).sub(1)

						const gammas = tf.tensor4d(
							group.map(({ input }) => input.adjustedGamma),
							[group.length, 1, 1, 1],
						)
						return crops.div(255).pow(gammas).mul(2).sub(1)
					}),
				),
				0,
			),
		)
		const results = Array<FaceScoringResult | undefined>(inputs.length).fill(undefined)

		try {
			for (let offset = 0; offset < orderedInputs.length; offset += batchSize) {
				const batch = orderedInputs.slice(offset, offset + batchSize)
				const outputTensor = tf.tidy(() => {
					const batchTensor = inputTensor.slice(
						[offset, 0, 0, 0],
						[batch.length, 224, 224, 3],
					)
					const [eyeTensor, focusTensor, expressionTensor] = model.execute(batchTensor, [
						"Identity",
						"Identity_2",
						"Identity_1",
					]) as tf.Tensor[]
					if (!eyeTensor || !focusTensor || !expressionTensor)
						throw new Error("Face scoring returned no output")
					return tf.concat([eyeTensor, focusTensor, expressionTensor], 1)
				})
				let output: Float32Array
				try {
					output = (await outputTensor.data()) as Float32Array
				} finally {
					outputTensor.dispose()
				}

				batch.forEach(({ input, resultIndex }, index) => {
					const offset = index * 15
					results[resultIndex] = this.createResult(
						output.slice(offset, offset + 13),
						output[offset + 13] as number,
						output[offset + 14] as number,
						input.rect,
						input.confidence,
					)
				})
			}
		} finally {
			inputTensor.dispose()
		}

		const completedResults = results.flatMap((result) => (result ? [result] : []))
		if (completedResults.length !== inputs.length)
			throw new Error("Face scorer returned an incomplete batch")
		return completedResults
	}

	// Although not used, keeping this for future if we need to use higher resolution image input,
	// because TFJS has issue with large image bitmap, so we need to crop face region using cpu first.
	public async processBitmapCrop(
		image: ImageBitmap,
		options: {
			rects: Rect[]
			confidences: (number | undefined)[]
			adjustedGammas: number[]
		},
	): Promise<FaceScoringResult[]> {
		const { rects, confidences, adjustedGammas } = options
		const results: FaceScoringResult[] = []

		const imgSize: [number, number] = [image.width, image.height]

		for (let i = 0; i < rects.length; i++) {
			const rect = rects[i] as Rect

			results.push(
				await this.getFaceScoresBitmapCrop(
					image,
					rect,
					confidences[i] ?? 1,
					imgSize,
					adjustedGammas[i] ?? 1,
				),
			)
		}

		return results
	}

	@measured()
	async getFaceScoresBitmapCrop(
		image: ImageBitmap,
		rect: Rect,
		confidence: number,
		imgSize: [number, number],
		adjustedGamma: number,
	) {
		const model = await this.loadModel()

		const croppedBitmap = await createImageBitmap(
			image,
			Math.round(rect.x * imgSize[0]),
			Math.round(rect.y * imgSize[1]),
			Math.round(rect.width * imgSize[0]),
			Math.round(rect.height * imgSize[1]),
		)

		// Yuhe: this seems to give worse results (more blurry/occluded) than resizing to 224x224 using TFJS
		// const resizedBitmap = resizeImageBitmapToBitmap(croppedBitmap, 224, 224, "high")

		const isLarge = rect.width > 0.5 && rect.height > 0.5 // usually for no face photo

		try {
			const outputTensor = tf.tidy(() => {
				const tensor = this.getGammaCorrectedTensor(croppedBitmap, adjustedGamma)
				const imageTensor = this.preprocess(tensor, [224, 224], undefined)
				const [eyeTensor, focusTensor, expressionTensor] = model.execute(imageTensor, [
					"Identity",
					"Identity_2",
					"Identity_1",
				]) as tf.Tensor[]
				if (!eyeTensor || !focusTensor || !expressionTensor)
					throw new Error("Face scoring returned no output")
				return tf.concat([eyeTensor, focusTensor, expressionTensor], 1)
			})
			let output: Float32Array
			try {
				output = (await outputTensor.data()) as Float32Array
			} finally {
				outputTensor.dispose()
			}

			return this.createResult(
				output.slice(0, 13),
				output[13] as number,
				output[14] as number,
				rect,
				confidence,
				isLarge,
			)
		} finally {
			croppedBitmap.close()
		}
	}

	private createResult(
		eyeStatusProbability: Float32Array,
		focusScoreRaw: number,
		expressionScoreRaw: number,
		rect: Rect,
		confidence: number,
		isLarge = rect.width > 0.5 && rect.height > 0.5,
	): FaceScoringResult {
		const expScore = (expressionScoreRaw - 0.4) * 1.15
		const focusScore = isLarge ? (focusScoreRaw * 9.5) / 5 : (focusScoreRaw - 0.2) * 2.15
		const focusScoreFloat = clamp(Math.round(focusScore * 10) / 10, 0, 10)
		const { eyeStatus, eyeScore, expressionScore } = this.getEyeStatusAndScore(
			eyeStatusProbability,
			{ focusScoreFloat, expScore, confidence },
		)

		logCullingInfo("FaceScoring model result", {
			eyeStatusProbability,
			eyeStatusName: EyeStatus[eyeStatus],
			expScore,
			eyeScore,
			expressionScore,
			focusScoreFloat,
			focusScoreRaw,
			rect,
		})

		return { eyeStatus, eyeEstScore: eyeScore, expressionScore, focusScoreFloat }
	}

	private getEyeStatusAndScore(
		eyeStatusProbability: Float32Array,
		info: { focusScoreFloat: number; confidence: number; expScore: number },
	) {
		const probsArray = Array.from(eyeStatusProbability)
		// This is model output order
		const classes = [
			EyeStatus.closed,
			EyeStatus.midBlink,
			EyeStatus.barelyOpen,
			EyeStatus.halfOpen,
			EyeStatus.fullyOpen,
			EyeStatus.posingLookDown,
			EyeStatus.posingSmile,
			EyeStatus.posingBeClose,
			EyeStatus.sunglasses,
			EyeStatus.occluded,
			EyeStatus.tooBlurry,
			EyeStatus.notAFace,
			EyeStatus.squinting,
		]

		// Perform argsort: get indices sorted by probability in descending order
		const sortedIndices = probsArray
			.map((prob, index) => ({ index, prob }))
			.sort((a, b) => b.prob - a.prob)
			.map((item) => item.index)

		// Get top-k indices
		const topKIndices = sortedIndices.slice(0, 3)

		// Compute the final score
		let eyeScore = 0
		let probSum = 0
		topKIndices.forEach((statusIndex) => {
			const status = classes[statusIndex] as EyeStatus
			const score = scoreDict[status] || 0
			const prob = probsArray[statusIndex] as number
			eyeScore += score * prob
			probSum += prob
		})

		eyeScore /= Math.max(probSum, 0.5)

		const top1Index = topKIndices[0] as number
		let eyeStatus = classes[top1Index] as EyeStatus

		function isOccluded(eyeStatus: EyeStatus) {
			return (
				eyeStatus === EyeStatus.occluded ||
				eyeStatus === EyeStatus.notAFace ||
				eyeStatus === EyeStatus.tooBlurry
			)
		}

		if (info.focusScoreFloat < 2 && !isOccluded(eyeStatus)) {
			eyeStatus = EyeStatus.tooBlurry
		}

		// occluded face tends to have very low expression score by the model, so we need to clamp it
		// because occluded should be considered as neutral instead of rejects
		let expScore = info.expScore
		if (isOccluded(eyeStatus)) {
			expScore = Math.max(expScore, 2.5)
		}
		const expressionScore = clamp(expScore * 0.5 + eyeScore * 0.5, 0.5, 5.5)

		if (eyeStatus === EyeStatus.closed && expressionScore > 2.3) {
			eyeStatus = EyeStatus.barelyOpen
		}

		return { eyeStatus, eyeScore, expressionScore }
	}

	private preprocess(tensor: tf.Tensor, size: [number, number], rect: Rect | undefined) {
		return tf.tidy(() => {
			if (rect) {
				const roi = [rect.y, rect.x, rect.y + rect.height, rect.x + rect.width]

				const tensor4d = tensor.expandDims()
				return tf.image
					.cropAndResize(tensor4d as tf.Tensor4D, [roi], [0], size)
					.div(127.5)
					.sub(1)
			} else {
				return tf.image
					.resizeBilinear(tensor as tf.Tensor3D, size)
					.expandDims()
					.div(127.5)
					.sub(1)
			}
		})
	}

	private getGammaCorrectedTensor(bitmap: ImageBitmap, gamma: number) {
		return tf.tidy(() => {
			const tensor = tf.browser.fromPixels(bitmap)
			if (gamma != 1) {
				return tensor.div(255).pow(tf.scalar(gamma)).mul(255)
			}
			return tensor
		})
	}
}
